import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Separator } from "@/components/ui/separator";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function AdminVisuals() {
  const [visuals, setVisuals] = useState([]);
  const [frequencyHz, setFrequencyHz] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const loadVisuals = async () => {
    try {
      const res = await axios.get(`${API}/frequency-visuals`);
      setVisuals(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load visuals");
    }
  };

  useEffect(() => {
    loadVisuals();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${API}/upload-visual-file`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!frequencyHz || isNaN(frequencyHz)) {
      toast.error("Please enter a valid frequency");
      return;
    }

    if (!imageFile && !videoFile) {
      toast.error("Please select an image or video file");
      return;
    }

    setUploading(true);
    try {
      let imageUrl = null;
      let videoUrl = null;

      if (imageFile) {
        imageUrl = await uploadFile(imageFile);
      }

      if (videoFile) {
        videoUrl = await uploadFile(videoFile);
      }

      await axios.post(`${API}/frequency-visuals`, {
        frequency_hz: parseFloat(frequencyHz),
        image_url: imageUrl,
        video_url: videoUrl,
        description: description || null,
      });

      toast.success("Visual uploaded successfully!");
      setFrequencyHz("");
      setDescription("");
      setImageFile(null);
      setVideoFile(null);
      setImagePreview(null);
      loadVisuals();
    } catch (e) {
      console.error(e);
      toast.error("Failed to upload visual");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (visualId) => {
    if (!confirm("Are you sure you want to delete this visual?")) return;

    try {
      await axios.delete(`${API}/frequency-visuals/${visualId}`);
      toast.success("Visual deleted");
      loadVisuals();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete visual");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Admin Visual Upload</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Upload frequency-specific visuals for enhanced sigil & hemi syncs</p>
      </div>

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle>Upload New Visual</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="frequency">Frequency (Hz)</Label>
              <Input
                id="frequency"
                type="number"
                step="0.01"
                placeholder="e.g., 528"
                value={frequencyHz}
                onChange={(e) => setFrequencyHz(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                type="text"
                placeholder="e.g., Love frequency, DNA repair"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="image">Image File</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img src={imagePreview} alt="Preview" className="max-w-xs rounded border" />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="video">Video File (Optional)</Label>
              <Input
                id="video"
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
              />
              {videoFile && (
                <p className="text-sm text-muted-foreground mt-1">Selected: {videoFile.name}</p>
              )}
            </div>

            <Button type="submit" disabled={uploading} className="w-full">
              {uploading ? "Uploading..." : "Upload Visual"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Separator />

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle>Uploaded Visuals ({visuals.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {visuals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No visuals uploaded yet</p>
            ) : (
              visuals.map((visual) => (
                <div
                  key={visual.id}
                  className="flex items-start gap-4 p-4 border rounded-lg bg-black/20"
                >
                  {visual.image_url && (
                    <img
                      src={visual.image_url}
                      alt={`${visual.frequency_hz} Hz`}
                      className="w-24 h-24 object-cover rounded"
                    />
                  )}
                  <div className="flex-1">
                    <div className="font-semibold text-lg text-[#a4ff2d]">
                      {visual.frequency_hz} Hz
                    </div>
                    {visual.description && (
                      <p className="text-sm text-gray-400 mt-1">{visual.description}</p>
                    )}
                    {visual.video_url && (
                      <p className="text-xs text-muted-foreground mt-1">📹 Video included</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Uploaded: {new Date(visual.created_at).toLocaleString()}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(visual.id)}
                  >
                    Delete
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
